import { RootState } from "common/reducers";
import { byId as imageById, imageOverviewById, imageUrlFromId } from "common/selectors/image.selectors";
import { Nullable } from "common/types";
import { List } from "immutable";
import { createSelector } from "reselect";

const rootKey = "estimateLineItemOptions";

/**
 * Loads byId key from estimateLineItemOptions
 *
 * @param {RootState} state The RootState
 * @returns {Map<number, EstimateLineItemOptionRecord>} Map<number, EstimateLineItemOptionRecord>
 */
export const byId = (state: RootState) => state.getIn([rootKey, "byId"]);

/**
 * Loads the line item by id
 *
 * @param {RootState} state The RootState
 * @returns
 */
export const estimateLineItemOption = (state: RootState, props: { estimateLineItemOptionId: number }) =>
  byId(state).get(props.estimateLineItemOptionId);

/**
 * Loads the line item options by ids, filtering out destroyed options for display.
 *
 * - If `fullEstimate` is true, all options are returned regardless of their `_destroy` flag.
 * - Otherwise (the display default), options with `_destroy` set to true are excluded.
 *
 * @param {RootState} state The RootState
 * @param {{ estimateLineItemOptionIds: List<number>; fullEstimate?: boolean }} props The option ids and filter options
 * @returns {List<EstimateLineItemOptionRecord>} List<EstimateLineItemOptionRecord>
 */
export const estimateLineItemOptions = (
  state: RootState,
  props: { estimateLineItemOptionIds: List<number>; fullEstimate?: boolean },
) =>
  props.estimateLineItemOptionIds
    .map((id) => estimateLineItemOption(state, { estimateLineItemOptionId: id }))
    // Display (default) returns only resolved, non-destroyed options so the row never renders a
    // destroyed leftover or a phantom (unresolved-id) entry. `fullEstimate` returns the raw mapped
    // set — holes included — for the totals/serialization paths (which tolerate/diagnose missing ids).
    .filter((o) => props.fullEstimate || (!!o && !o._destroy));

/**
 * Gets the current unsaved ID for creating new estimate line item options
 *
 * @param {RootState} state The RootState
 * @returns {number} The current unsaved ID (negative number)
 */
export const unsavedId = (state: RootState): number => state.getIn([rootKey, "unsavedId"]) as number;

export const lineItemOptionImageUrl = createSelector(
  [estimateLineItemOption, imageOverviewById, imageById],
  (option, overviewById, imagesById): Nullable<string> =>
    option?.product_option_image_urls?.first() ||
    option?.product_option_image_ids
      ?.map((id: number) => imageUrlFromId(overviewById, imagesById, id))
      .filter((url: Nullable<string>): url is string => !!url)
      .first(),
);
